// Imports
import React, { useCallback, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import PlaceUpserter, { Address } from '../../../../../../../shareables/front-end/place-upserter';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetAddressResponse } from '../../../../../../../shareables/types/api/main/website/account/address/get';
import { GetAddressesResponse } from '../../../../../../../shareables/types/api/main/website/account/addresses/get';
import { GetRegistrationResponse } from '../../../../../../../shareables/types/api/main/website/settings/registration/get';
import removePersistedFormEntry from '../../../../../../../utils/remove-persisted-form-entry';
import Panel from '../../../page-types/shared-components/panel';


// Define the accepted props
interface Props {
	onClose: () => void;
	onCompletion: () => void;
	isFormOpen: boolean;
	addressID?: GetAddressesResponse[0]['id'];
	storybookRegistrationSettings?: GetRegistrationResponse;
}


// Function component
const UpsertAddress: React.FC<Props> = ({
	addressID,
	isFormOpen,
	onClose,
	onCompletion,
	storybookRegistrationSettings,
}) => {
	// Use state
	const [registrationSettings, setRegistrationSettings] = useState<GetRegistrationResponse | null>(
		storybookRegistrationSettings ?? null
	);
	
	const [address, setAddress] = useState<GetAddressResponse>();
	const [shouldAnimateOpen, setShouldAnimateOpen] = useState(false);
	const [enableAutoFocus, setEnableAutoFocus] = useState(false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const accountType = useAppSelector((state) => state.self.member?.account.type);
	
	
	// Load registration settings on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetRegistrationResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/settings/registration`,
				completion: (json) => {
					setRegistrationSettings(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// Load address if editing
	useEffect(() => {
		// Reset state and return early
		if (!addressID) {
			setAddress(undefined);
			return;
		}
		
		
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetAddressResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/account/address/${addressID}`,
				completion: (json) => {
					setAddress(json.response);
				},
			})
		);
	}, [dispatch, addressID]);
	
	
	// Handle animation
	//  -> Delays animation until data loads and defers to the next render cycle so inner content is ready
	useEffect(() => {
		if (!isFormOpen) {
			setShouldAnimateOpen(false);
			setEnableAutoFocus(false);
			return;
		}
		
		if (!addressID || address) {
			setShouldAnimateOpen(true);
		}
	}, [isFormOpen, address, addressID]);
	
	
	// Handle animation end
	//  -> Delays autofocus so it's not lost while the height is animating
	const handleAnimationEnd = useCallback(() => {
		if (shouldAnimateOpen && !addressID) {
			setEnableAutoFocus(true);
		}
	}, [shouldAnimateOpen, addressID]);
	
	
	// If loading, then display nothing
	if (registrationSettings === null) {
		return null;
	}
	
	
	// Construct present fields
	const presentFields: (keyof Address)[] = [
		'countryCode',
		'division1',
		'division2',
		'mailingAddress',
		'postalCode',
		'regionID',
		'streetAddress',
		'type',
		'unitNumber',
		'unitType',
		'homePhone',
	];
	
	if (registrationSettings.extraAddressDetail) {
		presentFields.push('extraAddressDetail');
	}
	
	if (accountType?.requireBillingAddress) {
		presentFields.push('billingAddress');
	}
	
	if (registrationSettings.locations.length > 1) {
		presentFields.push('alternateLocationID');
	}
	
	
	// Function that handles closing and resetting the form
	const closeAndResetForm = () => {
		// Reset form
		removePersistedFormEntry(
			`${address ? 'PUT' : 'POST'} /website/${window.location.hostname}/account/address${addressID ? `/${addressID}` : ''}`
		);
		
		
		// Close form
		onClose();
	};
	
	
	// Function that handles completing and resetting the form
	const completeAndResetForm = () => {
		// Reset form
		removePersistedFormEntry(
			`${address ? 'PUT' : 'POST'} /website/${window.location.hostname}/account/address${addressID ? `/${addressID}` : ''}`
		);
		
		
		// Complete form
		onCompletion();
	};
	
	
	// Return JSX
	return (
		<AnimateHeight
			height={shouldAnimateOpen ? 'auto' : 0}
			duration={400}
			animateOpacity
			onHeightAnimationEnd={handleAnimationEnd}
		>
			<Panel closeOnClick={closeAndResetForm}>
				<PlaceUpserter
					key={addressID ?? (isFormOpen ? 'new' : 'closed')}
					initialPlace={address}
					onCompletion={completeAndResetForm}
					onCancel={closeAndResetForm}
					uri={`/website/${window.location.hostname}/account/address${address?.id ? `/${address.id}` : ''}`}
					action={address ? 'PUT' : 'POST'}
					locations={registrationSettings.locations}
					extraAddressDetail={registrationSettings.extraAddressDetail ?? undefined}
					optionalUnitNumber={registrationSettings.unitNumberRequirement === 'OPTIONAL'}
					hiddenUnitNumber={registrationSettings.unitNumberRequirement === 'HIDDEN'}
					presentFields={presentFields}
					fallbackCountry={registrationSettings.twoLetterCode}
					container={({ children }) => <React.Fragment>{children}</React.Fragment>}
					submitButtonText={addressID ? 'Save address' : 'Add address'}
					autoFocus={enableAutoFocus && !addressID}
				/>
			</Panel>
		</AnimateHeight>
	);
};

export default UpsertAddress;
